// Screen-side copy of the booking form delete rule (helpdesk 10750, bop-documentation #186),
// shared by Booking Forms (BookingForms2.vue) and Charges (InventoryLines.vue, Bhold list).
// Pure functions, no Vue and no network, so both screens grey out Delete the same way and the
// rule can be unit tested.
//
// The rule agreed with Accounts on 27 Oct 2026, the same one tp_server applies in
// bookingforms_delete and in inventorylines_delete for a TP BOOKING FORM row:
//   - an invoiced form is never deletable, not even by an admin
//   - deleting needs bookingforms.delete or bookingforms.admin
//   - the form's own agent (initials match) may delete it, submitted or not
//   - another agent's form only for bookingforms.admin, and only once that agent has left.
//     The screen cannot tell whether an agent has left, so admins may select other agents'
//     forms and the server's refusal is shown if the agent is still active.
// Rows that are not booking forms are not this module's business: isbookingform() tells the
// Charges screen which selected rows the rule applies to.

export const BOOKINGFORM_PLATFORM = "TP BOOKING FORM";

function clean(value) {
  return value === null || value === undefined ? "" : String(value).trim();
}

// Setup_Users.[permissions] reaches the screen as the stored string, which is a JSON array
// ('["tickets.view","bookingforms.admin"]', written by UsersEdit/Setup_UserEdit). Older rows
// may be a plain comma list. Split either into exact names, so 'bookingforms.admin' is not
// matched by a longer name that happens to contain it.
export function permissions(user) {
  if (!user || !user.permissions) return [];
  if (Array.isArray(user.permissions)) return user.permissions.map((p) => clean(p));
  let text = String(user.permissions).trim();
  if (text.startsWith("[")) {
    try {
      let list = JSON.parse(text);
      if (Array.isArray(list)) return list.map((p) => clean(p));
    } catch (error) {
      // not valid JSON: fall through to the comma split, stripping brackets and quotes
    }
  }
  return text
    .split(",")
    .map((p) => p.trim().replace(/^[[\s"']+|[\]\s"']+$/g, ""))
    .filter((p) => p != "");
}

export function isbookingform(row) {
  return !!row && clean(row.platform).toUpperCase() == BOOKINGFORM_PLATFORM;
}

export function isadmin(user) {
  return permissions(user).includes("bookingforms.admin");
}

export function candeleteforms(user) {
  return isadmin(user) || permissions(user).includes("bookingforms.delete");
}

export function isinvoiced(row) {
  return !!row && (clean(row.invoiceno) != "" || clean(row.status).toLowerCase() == "invoiced");
}

export function isown(row, user) {
  if (!row) return false;
  let agent = clean(row.agent).toUpperCase();
  let initials = clean(user ? user.initials : "").toUpperCase();
  return initials != "" && agent == initials;
}

export function candelete(row, user) {
  if (!row) return false;
  if (isinvoiced(row)) return false;
  if (!candeleteforms(user)) return false;
  if (isadmin(user)) return true;
  return isown(row, user);
}

// Tooltip for a greyed-out Delete button
export function deletetooltip(user) {
  if (!candeleteforms(user)) return "You do not have permission to delete booking forms";
  return isadmin(user)
    ? "Invoiced booking forms cannot be deleted; other agents' forms only if the agent has left"
    : "You can delete your own booking forms unless they have been invoiced";
}

// HTML message explaining why Delete is not available for these rows, or null when every
// row passes. Pass only booking form rows.
export function deletemessage(rows, user) {
  let forms = (rows || []).filter((row) => !!row);
  if (forms.length == 0) return null;
  if (!candeleteforms(user)) {
    return "<span class='text-danger'>Delete is not available: you do not have permission to delete booking forms.</span>";
  }
  let blocked = forms.filter((row) => !candelete(row, user));
  if (blocked.length == 0) return null;
  let invoiced = blocked.filter((row) => isinvoiced(row));
  let others = blocked.filter((row) => !isinvoiced(row));
  let reasons = [];
  if (invoiced.length > 0) {
    reasons.push(
      invoiced.length == 1
        ? "booking form " + clean(invoiced[0].referenceno) + " has been invoiced"
        : invoiced.length + " selected booking forms have been invoiced"
    );
  }
  if (others.length > 0) {
    reasons.push(
      others.length == 1
        ? "booking form " + clean(others[0].referenceno) + " belongs to another agent"
        : others.length + " selected booking forms belong to another agent"
    );
  }
  return (
    "<span class='text-danger'>Delete is not available: " +
    reasons.join(" and ") +
    ". " +
    deletetooltip(user) +
    ".</span>"
  );
}
